import React, { useMemo, useState } from 'react'
import { Font } from '../../../../types/Font'
import Text from '../../../../components/Text'
import Input from '../../../../components/Input'
import Button from '../../../../components/Button'
import styles from './CreateFontModal.module.css'
import cx from 'classnames'
import {
    faChevronDown,
    faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import Icon from '../../../../components/Icon'
import { useTranslation } from 'react-i18next'
import { useStoreData } from '../../../../hooks/useStoreData'
import * as yup from 'yup'
import DropInput from '../../../../components/DropInput'
import { ApiRoutes } from '../../../../services/ApiService'
import { closeModal } from '../../../../services/ModalService'
import { showToast } from '../../../../services/ToastService'

const CreateFontModal: React.FC<{ fonts: Font[]; onSubmit: () => void }> = ({
    fonts,
    onSubmit,
}) => {
    const [showWebsafe, setShowWebsafe] = useState<boolean>(false)
    const { t } = useTranslation(['settings'])

    const storeFontSchema = useMemo(() => {
        return {
            name: yup
                .string()
                .required()
                .min(3)
                .notOneOf(fonts.map((f) => f.name)),
        }
    }, [fonts])

    const {
        element,
        changeProp,
        changeFile,
        submit,
        isValid,
        clearData,
        error,
        files,
    } = useStoreData<Font>(ApiRoutes.FONTS, storeFontSchema)

    const handleSubmit = async () => {
        if (error) {
            showToast(error, 'error')
        } else if (isValid) {
            try {
                const res = await submit()
                if (res.status === 201) {
                    clearData()
                    closeModal()
                    showToast(t('fonts.create_modal.success'))
                    onSubmit()
                } else {
                    showToast(t('fonts.create_modal.error'), 'error')
                    onSubmit()
                }
            } catch (err: any) {
                if (err.response?.status === 422) {
                    const errorMessage = err.response?.data?.message || t('fonts.create_modal.error')
                    showToast(errorMessage, 'error')
                } else {
                    showToast(t('fonts.create_modal.error'), 'error')
                }
                onSubmit()
            }
        }
    }

    return (
        <div className={cx(styles['modal-wrapper'])}>
            <div className={'zr-w-100'}>
                <Text>{t('fonts.create_modal.sub_heading')}</Text>
            </div>
            <Input
                type={'text'}
                placeholder={t('fonts.labels.name')}
                property={'name'}
                value={element.name ?? ''}
                onChange={(_, value) => changeProp({ name: value })}
                classes={'zr-my-3'}
            />
            <div
                className={cx(styles['websafe-header'])}
                onClick={() => setShowWebsafe((prev) => !prev)}
            >
                <Text>{t('fonts.websafe.heading')}</Text>
                <Icon
                    icon={showWebsafe ? faChevronDown : faChevronRight}
                    color={'black'}
                    size={'sm'}
                />
            </div>
            <div
                className={cx(styles['websafe-wrapper'])}
                style={{ maxHeight: !showWebsafe ? '0px' : '100vh' }}
            >
                {t('fonts.websafe.content')}
            </div>
            <div className={cx(styles['upload-label'])}>
                {t('fonts.labels.file')}
            </div>
            <div className={'zr-w-100'}>
                <DropInput
                    accept={'.ttf,.woff,.woff2'}
                    onFileChange={(file) => changeFile(file)}
                />
                <div className={cx(styles['text-note'])}>
                    {t('fonts.websafe.desc')}
                </div>
            </div>
            {files.length > 0 && (
                <div className={cx(styles['chosen-font'])}>
                 {files[0].name}
                </div>
            )}
            <Button
                hierarchy={'primary'}
                text={t('fonts.create_modal.action')}
                fn={() => handleSubmit()}
                classes={'zr-w-100 zr-mx-4'}
                disabled={!isValid}
            />
        </div>
    )
}

export default CreateFontModal
